import { Injectable } from '@angular/core';
import { BehaviorSubject, map, merge, Observable, startWith } from 'rxjs';
import { BreakpointObserver } from '@angular/cdk/layout';

import { NavigationService } from '@tablesready/frontend-navigation';

import {
  desktopMenuBreakpoint,
  floorPlanRightSidebarBreakpoint, desktopSettingsMenuBreakpoint, xsBreakpoint,
  shortHeightBreakpoint,
} from './frontend-breakpoints.constants';

@Injectable({ providedIn: 'root' })
export class FrontendBreakpointsService {
  mainNavSidebarWidth$: Observable<number>;

  private readonly mainNavSidebarWidthSource$ = new BehaviorSubject<number>(0);

  constructor(
    private readonly breakpointObserver: BreakpointObserver,
    private readonly navService: NavigationService,
  ) {
    this.mainNavSidebarWidth$ = this.mainNavSidebarWidthSource$.asObservable();
  }

  get isDesktopMode(): boolean {
    return this.breakpointObserver.isMatched(desktopMenuBreakpoint);
  }

  get isSettingsDesktopMode(): boolean {
    return this.breakpointObserver.isMatched(desktopSettingsMenuBreakpoint);
  }

  observeXsBreakpoint(): Observable<boolean> {
    return this.breakpointObserver.observe(xsBreakpoint)
      .pipe(
        startWith(null),
        map(() => this.breakpointObserver.isMatched(xsBreakpoint)),
      );
  }

  observeDesktopMode(): Observable<boolean> {
    return merge(
      this.breakpointObserver.observe([desktopMenuBreakpoint]),
      this.navService.navEvents$,
    )
      .pipe(
        startWith(null),
        map(() => this.isDesktopMode),
      );
  }

  observeSettingsDesktopMode(): Observable<boolean> {
    return merge(
      this.breakpointObserver.observe([desktopSettingsMenuBreakpoint]),
      this.navService.navEvents$,
    )
      .pipe(
        startWith(null),
        map(() => this.isSettingsDesktopMode),
      );
  }

  observeShortHeight(): Observable<boolean> {
    return this.breakpointObserver.observe(shortHeightBreakpoint)
      .pipe(
        startWith(null),
        map(() => this.breakpointObserver.isMatched(shortHeightBreakpoint)),
      );
  }

  observeFloorPlanRightSidebar(): Observable<boolean> {
    return this.breakpointObserver.observe(floorPlanRightSidebarBreakpoint)
      .pipe(
        startWith(null),
        map(() => this.breakpointObserver.isMatched(floorPlanRightSidebarBreakpoint)),
      );
  }

  setMainNavSidebarWidth(value: number): void {
    this.mainNavSidebarWidthSource$.next(value);
  }
}
